import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Star, Heart, Minus, Plus, BadgeCheck } from "lucide-react";
import { toast } from "sonner";

const Stars = ({ n, size = 13 }) => (
  <span className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} size={size} strokeWidth={1.5} className={i <= n ? "fill-luxa-gold text-luxa-gold" : "text-luxa-espresso/25"} />
    ))}
  </span>
);

export default function ProductDetail() {
  const { slug } = useParams();
  const { add } = useCart();
  const { user, setUser } = useAuth();
  const [p, setP] = useState(null);
  const [img, setImg] = useState(0);
  const [qty, setQty] = useState(1);
  const [form, setForm] = useState({ rating: 5, title: "", body: "" });

  const load = () => api.get(`/products/${slug}`).then((r) => { setP(r.data); setImg(0); }).catch(() => setP(false));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug]);

  if (p === false) return <div className="py-32 text-center font-serif text-2xl">Product not found.</div>;
  if (!p) return <div className="py-32 text-center overline text-luxa-espresso/40">Loading…</div>;

  const wished = user?.wishlist?.includes(p.id);
  const toggleWish = async () => {
    if (!user) return toast.error("Sign in to save pieces to your wishlist.");
    const { data } = await api.post(`/account/wishlist/${p.id}`);
    setUser({ ...user, wishlist: data.wishlist });
    toast.success(data.wishlist.includes(p.id) ? "Saved to wishlist" : "Removed from wishlist");
  };

  const submitReview = async (e) => {
    e.preventDefault();
    if (!user) return toast.error("Sign in to leave a review.");
    try {
      await api.post("/reviews", { product_id: p.id, ...form });
      toast.success("Thank you — your review is awaiting moderation.");
      setForm({ rating: 5, title: "", body: "" });
    } catch (err) { toast.error(errMsg(err.response?.data?.detail)); }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-12 lg:py-20" data-testid="product-page">
      <div className="overline text-luxa-espresso/40 mb-10">
        <Link to="/shop" className="hover:text-luxa-gold">Shop</Link> / {p.category} / {p.name}
      </div>
      <div className="grid lg:grid-cols-2 gap-10 lg:gap-20">
        <div className="lg:sticky lg:top-28 self-start">
          <div className="bg-luxa-sand aspect-[4/5] overflow-hidden">
            <img src={p.images?.[img]} alt={p.name} className="w-full h-full object-cover" data-testid="product-main-image" />
          </div>
          <div className="mt-4 flex gap-4">
            {p.images?.map((src, i) => (
              <button key={i} onClick={() => setImg(i)} data-testid={`product-thumb-${i}`}
                className={`w-20 h-24 overflow-hidden border transition-opacity duration-300 ${i === img ? "border-luxa-brown" : "border-transparent opacity-60 hover:opacity-100"}`}>
                <img src={src} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>

        <div>
          <p className="overline text-luxa-brown">{p.category}</p>
          <h1 className="mt-4 font-serif text-4xl sm:text-5xl tracking-tighter leading-none">{p.name}</h1>
          <div className="mt-5 flex items-center gap-4">
            <Stars n={Math.round(p.rating)} />
            <span className="text-xs text-luxa-espresso/50">{p.rating || "—"} · {p.reviews?.length || 0} reviews</span>
          </div>
          <p className="mt-7 overline text-luxa-brown" data-testid="product-price">Price on request</p>
          <p className="mt-7 text-luxa-espresso/70 leading-relaxed">{p.description}</p>

          <div className="mt-10 flex items-center gap-4">
            <div className="flex items-center border border-luxa-sand">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="px-4 py-4" data-testid="qty-dec"><Minus size={13} strokeWidth={1.5} /></button>
              <span className="w-8 text-center text-sm" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty(qty + 1)} className="px-4 py-4" data-testid="qty-inc"><Plus size={13} strokeWidth={1.5} /></button>
            </div>
            <Button data-testid="add-to-cart-button" disabled={p.stock === 0} onClick={() => { add(p, qty); toast.success(`${p.name} added to your bag`); }}
              className="flex-1 rounded-none bg-luxa-brown hover:bg-luxa-brown/90 text-white h-14 overline">
              {p.stock === 0 ? "Sold Out" : "Add to Bag"}
            </Button>
            <button onClick={toggleWish} data-testid="wishlist-button" aria-label="Wishlist"
              className="h-14 w-14 border border-luxa-sand flex items-center justify-center hover:border-luxa-brown">
              <Heart size={17} strokeWidth={1.5} className={wished ? "fill-luxa-brown text-luxa-brown" : ""} />
            </button>
          </div>
          <p className="mt-4 overline text-luxa-espresso/45">
            {p.stock > 0 ? `${p.stock} in stock · ships in 2 business days` : "Join the waitlist for the next run"}
          </p>

          <Accordion type="single" collapsible defaultValue="material" className="mt-12">
            <AccordionItem value="material" className="border-luxa-sand">
              <AccordionTrigger className="font-serif text-lg hover:no-underline">Material & Construction</AccordionTrigger>
              <AccordionContent className="text-luxa-espresso/70 leading-relaxed">{p.material}</AccordionContent>
            </AccordionItem>
            <AccordionItem value="care" className="border-luxa-sand">
              <AccordionTrigger className="font-serif text-lg hover:no-underline">Care Guide</AccordionTrigger>
              <AccordionContent className="text-luxa-espresso/70 leading-relaxed">{p.care}</AccordionContent>
            </AccordionItem>
            <AccordionItem value="ship" className="border-luxa-sand">
              <AccordionTrigger className="font-serif text-lg hover:no-underline">Shipping & Repairs</AccordionTrigger>
              <AccordionContent className="text-luxa-espresso/70 leading-relaxed">
                Complimentary shipping on every order. Ten-year repair promise on every stitch.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </div>

      <section className="mt-28 grid lg:grid-cols-[1fr_1fr] gap-16" data-testid="reviews-section">
        <div>
          <h2 className="font-serif text-2xl sm:text-3xl tracking-tight">Owner reviews</h2>
          <div className="mt-8 divide-y divide-luxa-espresso/10">
            {(p.reviews || []).map((r) => (
              <div key={r.id} className="py-6" data-testid={`review-${r.id}`}>
                <div className="flex items-center gap-3">
                  <Stars n={r.rating} />
                  {r.verified_buyer && (
                    <span className="overline text-luxa-brown flex items-center gap-1"><BadgeCheck size={13} strokeWidth={1.5} /> Verified buyer</span>
                  )}
                </div>
                {r.title && <p className="mt-3 font-serif text-lg">{r.title}</p>}
                <p className="mt-2 text-luxa-espresso/70 leading-relaxed">{r.body}</p>
                <p className="mt-3 overline text-luxa-espresso/40">{r.author}</p>
              </div>
            ))}
            {(p.reviews || []).length === 0 && <p className="py-6 text-luxa-espresso/50">No reviews yet.</p>}
          </div>
        </div>
        <form onSubmit={submitReview} className="bg-luxa-shell border border-luxa-sand p-8" data-testid="review-form">
          <h3 className="font-serif text-xl">Write a review</h3>
          <div className="mt-5 flex gap-2">
            {[1, 2, 3, 4, 5].map((i) => (
              <button type="button" key={i} onClick={() => setForm({ ...form, rating: i })} data-testid={`review-star-${i}`}>
                <Star size={20} strokeWidth={1.5} className={i <= form.rating ? "fill-luxa-gold text-luxa-gold" : "text-luxa-espresso/25"} />
              </button>
            ))}
          </div>
          <Input data-testid="review-title" className="mt-5 rounded-none bg-luxa-shell border-luxa-sand h-11"
            placeholder="Headline" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          <Textarea data-testid="review-body" required rows={5} className="mt-3 rounded-none bg-luxa-shell border-luxa-sand"
            placeholder="How has it worn in?" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} />
          <Button data-testid="review-submit" type="submit" className="mt-5 rounded-none bg-luxa-espresso hover:bg-luxa-espresso/85 text-luxa-cream h-12 overline w-full">
            Submit Review
          </Button>
          {!user && <p className="mt-3 text-xs text-luxa-espresso/50">You must be signed in to review.</p>}
        </form>
      </section>
    </div>
  );
}
